/**
 * "Continue with Google" — the third-party sign-in control.
 *
 * ============================================================================
 * NEITHER BUTTON CAN SIGN YOU IN IN THIS BUILD, AND BOTH SAY SO WHEN PRESSED.
 * ============================================================================
 * There is no provider configuration (config.OAUTH_PROVIDERS reports Google
 * unconfigured while VITE_GOOGLE_CLIENT_ID is empty) and no
 * backend to exchange an authorization code for a session. So pressing one reaches
 * `authStore.signInWithProvider`, which reaches `authApi.signInWithProvider`, which
 * rejects with a 501 naming what is missing — see the long note in api/authApi.ts.
 *
 * THE ALTERNATIVE WOULD HAVE BEEN FAKING IT. A button that logs the demo user in
 * under Google's wordmark is a sign-in bypass that looks like a feature: it would
 * pass a click-through test, demo convincingly, and be discovered only when someone
 * shipped it. Refusing loudly is the correct behaviour for an unconfigured provider,
 * not a gap in the work.
 *
 * WHAT IS REAL HERE: the controls, their placement in the auth flow, the store action
 * they call, the provider list they render from, and the authorize URL that action's
 * API module can already build. What is missing is a client id and a server. Setting
 * the first makes the button live; the second is what lets it finish.
 *
 * Shared between the auth screen and anywhere else a sign-in choice appears, so the
 * provider and its wording have one definition.
 */
import { OAUTH_PROVIDERS } from '../../config';
import { c, DISABLED_OPACITY } from '../../styles/theme';
import type { AuthProviderId } from '../../types/domain';

const GOOGLE_PROVIDER = OAUTH_PROVIDERS.find((provider) => provider.id === 'google');

/**
 * Google's mark, drawn inline.
 *
 * lucide-react has no Google glyph — its `chrome` icon is a different product, and
 * using it would be labelling one company's button with another's logo. This is the
 * "G" in its four brand colours, which is what Google's own branding guidelines ask
 * a sign-in button to carry, at the same 15px the Apple glyph occupies.
 */
function GoogleMark() {
  return (
    <svg width={15} height={15} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86a5.4 5.4 0 0 1-5.07-3.73H.96v2.34A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.93 10.69a5.4 5.4 0 0 1 0-3.38V4.97H.96a9 9 0 0 0 0 8.06l2.97-2.34Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.97l2.97 2.34A5.4 5.4 0 0 1 9 3.58Z"
      />
    </svg>
  );
}

export interface ProviderButtonsProps {
  /**
   * Starts sign-in with a provider. Expected to reject in this build.
   *
   * Passed in rather than calling the store here so this component stays
   * presentational, like every other component under components/ — the screen owns
   * what a failure looks like, since that is where the error text lives.
   */
  onSelect: (provider: AuthProviderId) => void;
  /** Disables the button, e.g. while another auth attempt is in flight. */
  disabled?: boolean;
  /**
   * Wording above the buttons. "or continue with" on the auth screen.
   *
   * A separator rather than a heading: the email form above it is the primary path,
   * and these are an alternative to it.
   */
  dividerLabel?: string;
}

export default function ProviderButtons({
  onSelect,
  disabled,
  dividerLabel = 'or continue with',
}: ProviderButtonsProps) {
  return (
    <>
      {/* A rule with the label sitting in it. Two lines and centred text rather than
          a background-clipped single rule, so it works on any surface colour. */}
      <div className="lyx-auth-divider" aria-hidden="true">
        <span style={{ background: c.border }} />
        <span style={{ fontSize: 11, color: c.text2 }}>{dividerLabel}</span>
        <span style={{ background: c.border }} />
      </div>

      {/* Side by side where there is room, stacked on a narrow phone — see
          .lyx-auth-providers. Both labels stay whole either way, because
          "Continue with Google" turning into an ellipsis is the one thing a
          sign-in button must not do. */}
      <div className="lyx-auth-providers">
        {GOOGLE_PROVIDER ? (
          <button
            type="button"
            className="lyx-auth-provider"
            disabled={disabled}
            onClick={() => onSelect(GOOGLE_PROVIDER.id)}
            style={{
              background: c.surface2,
              border: `1px solid ${c.border}`,
              color: c.text1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? DISABLED_OPACITY : 1,
            }}
          >
            <GoogleMark />
            Continue with {GOOGLE_PROVIDER.label}
          </button>
        ) : null}
      </div>
    </>
  );
}
